'use client';

import { useEffect, useRef } from 'react';
import { useCartFlightState } from '@/hooks/use-cart-flight';
import {
  LAUNCH_AT_MS,
  boxCentre,
  flightStar,
  getCartFlightState,
  pickCartTarget,
  setCartFlightEnabled,
  sourceBoxNow,
  sourceGlow,
  type FlightBox,
  type RectLike,
} from '@/lib/cart-flight';
import { STAR_INK, STAR_WHITE, drawStarHead, drawStarTrail, type Point, type StarRgb } from '@/lib/shooting-star-draw';
import { hslToRgb } from '@/lib/hsl-color';

/** Same ceiling as the planets: past 2x the extra pixels cost more than they show. */
const MAX_DPR = 2;

type FlightTheme = 'dark' | 'light';

/**
 * The glow's colour for the theme. White on the dark sky, as the spec asks. On
 * a light page white is invisible, so the pressed button glows in the page's
 * own accent instead (the --primary triplet ThemeProvider paints, a seller's
 * on their page), falling back to the star ink if it cannot be read.
 */
function glowRgb(theme: FlightTheme): StarRgb {
  if (theme === 'dark') return STAR_WHITE;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
  const m = raw.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return STAR_INK;
  const { r, g, b } = hslToRgb({ h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) });
  return `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`;
}

/**
 * Every cart control the star could land on, as boxes. Read afresh each frame,
 * so a header that scrolls away or a search box that widens and shoves the
 * cart along is followed. An element that renders no box (a hidden branch)
 * has no client rects and is skipped.
 */
function cartTargetRects(): RectLike[] {
  const out: RectLike[] = [];
  document.querySelectorAll('[data-cart-target]').forEach((el) => {
    if (el.getClientRects().length > 0) out.push(el.getBoundingClientRect());
  });
  return out;
}

function roundedRect(ctx: CanvasRenderingContext2D, b: FlightBox): void {
  const r = Math.max(0, Math.min(b.radius, b.w / 2, b.h / 2));
  ctx.beginPath();
  ctx.moveTo(b.x + r, b.y);
  ctx.arcTo(b.x + b.w, b.y, b.x + b.w, b.y + b.h, r);
  ctx.arcTo(b.x + b.w, b.y + b.h, b.x, b.y + b.h, r);
  ctx.arcTo(b.x, b.y + b.h, b.x, b.y, r);
  ctx.arcTo(b.x, b.y, b.x + b.w, b.y, r);
  ctx.closePath();
}

/**
 * The pressed button fading into a glow: a translucent wash in the button's
 * own shape, a soft bloom around it and a bright rim, in `rgb` (white on the
 * dark sky, the accent on a light page). Drawn over the button rather than
 * done with a class on it, because a product card's button is replaced by the
 * re-render the add causes and would lose any class the moment it was added.
 */
function drawSourceGlow(ctx: CanvasRenderingContext2D, b: FlightBox, strength: number, dpr: number, rgb: StarRgb): void {
  ctx.save();
  roundedRect(ctx, b);
  // shadowBlur ignores the transform, so it is scaled to device pixels here.
  ctx.shadowColor = `rgba(${rgb}, ${0.75 * strength})`;
  ctx.shadowBlur = 18 * dpr;
  ctx.fillStyle = `rgba(${rgb}, ${0.3 * strength})`;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = `rgba(${rgb}, ${0.7 * strength})`;
  ctx.stroke();
  ctx.restore();
}

/**
 * The canvas, mounted only while a flight is in the air, so the page carries
 * no overlay and no animation loop between adds.
 */
function FlightCanvas({ theme }: { theme: FlightTheme }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Read by the loop, so a theme change mid-flight recolours it in place.
  const themeRef = useRef<FlightTheme>(theme);
  themeRef.current = theme;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    let dpr = 1;
    let width = 0;
    let height = 0;
    // Sized from the canvas's own box, not window.innerWidth, which includes
    // the scrollbar this fixed box does not cover: the bitmap was squeezed
    // into the narrower box and every point drawn in viewport coordinates
    // (the button's glow, the landing on the cart) drifted left by up to the
    // scrollbar's width. The same width and height bound the clear and the
    // cart target below.
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      width = canvas.clientWidth || document.documentElement.clientWidth;
      height = canvas.clientHeight || document.documentElement.clientHeight;
      const bw = Math.round(width * dpr);
      const bh = Math.round(height * dpr);
      if (canvas.width !== bw) canvas.width = bw;
      if (canvas.height !== bh) canvas.height = bh;
    };
    resize();
    // The window event still matters for a device pixel ratio change, which
    // leaves the CSS box alone. The observer catches a scrollbar appearing or
    // going mid-flight (the add lengthened the page, a dialog locked scroll),
    // which changes the box without firing a window resize.
    window.addEventListener('resize', resize);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
    observer?.observe(canvas);

    // Where each star left from, fixed at its launch: once it has left the
    // button it is travelling across the screen, not along with the page.
    const launchedFrom = new Map<number, Point>();
    let raf = 0;
    // The colours for the theme, worked out once per theme rather than every
    // frame (the light glow reads a CSS variable).
    let colourTheme: FlightTheme | null = null;
    let starRgb: StarRgb = STAR_WHITE;
    let sourceRgb: StarRgb = STAR_WHITE;

    const frame = () => {
      raf = requestAnimationFrame(frame);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      // Straight from the store rather than from React state, so a flight
      // added mid-loop is drawn on the next frame without a re-render.
      const { flights } = getCartFlightState();
      if (flights.length === 0) return;

      if (colourTheme !== themeRef.current) {
        colourTheme = themeRef.current;
        starRgb = colourTheme === 'light' ? STAR_INK : STAR_WHITE;
        sourceRgb = glowRgb(colourTheme);
      }
      const now = performance.now();
      const scroll = { x: window.scrollX, y: window.scrollY };
      const target = pickCartTarget(cartTargetRects(), { width, height });
      for (const id of Array.from(launchedFrom.keys())) {
        if (!flights.some((f) => f.id === id)) launchedFrom.delete(id);
      }

      for (const f of flights) {
        const elapsed = now - f.startedAt;
        const live = f.source && f.source.isConnected !== false ? f.source.getBoundingClientRect() : null;
        const box = sourceBoxNow(f, live, scroll);

        const glow = sourceGlow(elapsed);
        if (glow > 0) drawSourceGlow(ctx, box, glow, dpr, sourceRgb);

        let from = launchedFrom.get(f.id);
        if (!from && elapsed >= LAUNCH_AT_MS) {
          from = boxCentre(box);
          launchedFrom.set(f.id, from);
        }
        if (!from) continue;
        const star = flightStar(elapsed, from, target);
        if (!star) continue;
        drawStarTrail(ctx, star.trail, star.thickness, star.opacity, starRgb);
        drawStarHead(ctx, star.head.x, star.head.y, star.thickness, star.opacity, starRgb);
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      observer?.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[45] h-full w-full"
    />
  );
}

/**
 * The add-to-cart shooting star's overlay, mounted once at the root of the
 * layout, outside the content wrapper.
 *
 * `enabled` is useCosmicEffects().cartFlight: the star is feedback for an
 * add, so it plays whether or not the starfield is on, in either theme and on
 * a seller's page, but never against reduced motion or at checkout
 * (resolveCartFlight in src/lib/cosmic-effects.ts). The flag is handed to the
 * store, which refuses requests while it is off and drops any flight in the
 * air, with its dot hold, the moment it goes off. `theme` picks the palette:
 * white on a dark page, an ink star and an accent glow on a light one.
 *
 * z-[45]: over the header and storefront nav (z-40) and the product page's
 * sticky bars (z-30 and z-40), so the star is never hidden behind the bar it
 * is flying to; under dialogs (z-50) and toasts (z-[100]).
 *
 * The live region is outside the canvas's aria-hidden and always mounted, so
 * it is registered before anything is written into it (a region that appears
 * together with its text is often not announced). It speaks for the "Added to
 * Cart" toast, which a flight replaces.
 */
export function CartFlightLayer({ enabled, theme = 'dark' }: { enabled: boolean; theme?: FlightTheme }) {
  const { flights, announcement } = useCartFlightState();

  useEffect(() => {
    setCartFlightEnabled(enabled);
    return () => setCartFlightEnabled(false);
  }, [enabled]);

  return (
    <>
      {enabled && flights.length > 0 && <FlightCanvas theme={theme} />}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement && <span key={announcement.id}>{announcement.text}</span>}
      </div>
    </>
  );
}
